import { useQuery } from "@tanstack/react-query";
import {
  isUserDeleted,
  type PerformanceWithAccountFilter,
  canUseChildAccount,
  isUserFromCurrentAccount as isUserFromCurrentAccountUtil,
  getLinkNavigationProps,
} from "features/Library/Common/utils/performanceUtils";
import { type ColumnOption } from "interfaces/columnOptions";
import { useEffect, useMemo } from "react";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import styles from "./PeopleTable.module.scss";
import { getThreatDefensePerformancePeopleTable } from "../../queries/threatDefensePerformanceQueries";
import { faCircleCheck, faBullseyePointer, faEnvelopeOpen } from "@fortawesome/pro-solid-svg-icons";
import { type ThreatDefenseReportTablePeople } from "../types";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import { type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { FontAwesomeKitIcon, type KitIconDefinition } from "../../../../Common/utils/customFAIcons/FontAwesomeKitIcon";
import { customFontAwesomeIcons } from "../../../../Common/utils/customFAIcons/listOfIcons";
import { type Dispatch } from "redux";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";

type Props = {
  filter: PerformanceWithAccountFilter;
  campaignId: number;
  selectedSessionId: number;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
} & PropsFromRedux;

enum Icons {
  Sent = "Sent",
  Opened = "Opened",
  Clicked = "Clicked",
  Submitted = "Submitted",
}

const StatusToIcon: { [key in Icons]: KitIconDefinition } = {
  [Icons.Sent]: faCircleCheck,
  [Icons.Opened]: faEnvelopeOpen,
  [Icons.Clicked]: faBullseyePointer,
  [Icons.Submitted]: customFontAwesomeIcons.phishing,
};

const PeopleTable = ({
  filter,
  campaignId,
  selectedSessionId,
  accountId,
  setHasExportData,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
}: Props) => {
  const columns = useMemo<ColumnOption<ThreatDefenseReportTablePeople>[]>(
    () => [
      {
        name: "Name",
        width: 5,
        render(item) {
          const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
          const shouldShowLink =
            !isUserDeleted(item.UserName) && (!includeAccounts || childAccountEnabled || !childAccountId);
          const isUserFromCurrentAccount = isUserFromCurrentAccountUtil(
            item.AccountId,
            accountId,
            childAccountId,
            hasCreateAccountsAddOn,
          );
          const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
            childAccountEnabled,
            isUserFromCurrentAccount,
            childAccountId,
            fallbackAccountId: item.AccountId,
          });
          const userName = (
            <Tooltip target={<TextTruncate>{item.UserName}</TextTruncate>} content={`${item.UserName}`} />
          );
          return (
            <div>
              {shouldShowLink ? (
                <MoboLink
                  accountId={linkAccountId}
                  to={`/${RouteNames.peopleUsers}/${item.UserId}`}
                  className={styles.linkButton}
                  target={linkTarget}
                >
                  {userName}
                </MoboLink>
              ) : (
                userName
              )}
            </div>
          );
        },
      },
      {
        name: "Job Title",
        width: 5,

        render(item) {
          return (
            <Tooltip target={<TextTruncate>{item.JobTitle || "-"}</TextTruncate>} content={item.JobTitle || "-"} />
          );
        },
      },
      {
        name: "Department",

        width: 5,
        render(item) {
          return (
            <Tooltip target={<TextTruncate>{item.Department ?? "-"}</TextTruncate>} content={item.Department ?? "-"} />
          );
        },
      },
      {
        width: 6,
        name: "Email",

        render(item) {
          return <Tooltip target={<TextTruncate>{item.Email}</TextTruncate>} content={item.Email} />;
        },
      },
      {
        width: 4,
        name: "Status",

        render(item) {
          return (
            <div className={styles.icon}>
              <FontAwesomeKitIcon icon={StatusToIcon[item.Status]} className={styles[`${StatusToIcon[item.Status]}`]} />
              {item.Status}
            </div>
          );
        },
      },
    ],
    [accountId, includeAccounts, childAccountId, hasCreateAccountsAddOn],
  );

  const peopleQuery = useQuery({
    queryFn: getThreatDefensePerformancePeopleTable,
    queryKey: ["threatDefensePeopleTable", filter, campaignId, selectedSessionId],
  });

  useEffect(() => {
    setHasExportData({
      hasDataToExport: peopleQuery.isSuccess && peopleQuery.data.length > 0,
      tooltipContent: "No data available for export. This campaign send contains no data.",
    });
  }, [setHasExportData, peopleQuery]);

  return (
    <RequestStatusRenderer state={peopleQuery.status}>
      {peopleQuery.isSuccess && <GenericPerformanceList columns={columns} rows={peopleQuery.data} filter={filter} />}
    </RequestStatusRenderer>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PeopleTable);
export default ConnectedComponent;
